const byokContent = {
  title: "Bring Your Own Key (BYOK)",
  description: "Connect your OpenAI or Anthropic API key to Sortio and run AI file organization on any model they offer — billed to your provider account.",
  sections: [
    {
      id: "overview",
      title: "What is BYOK?",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Sortio normally uses its own managed AI, which draws from your plan's AI allowance. <strong>BYOK</strong> (Bring Your Own Key) lets you supply your own <strong>OpenAI</strong> or <strong>Anthropic</strong> API key so Sortio calls those models on your behalf, billed directly to your provider account.
        </p>

        <div class="grid md:grid-cols-2 gap-4 my-8">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center">
              <span class="mr-2">💳</span> Not metered by Sortio
            </h4>
            <p class="text-[#424245]">Calls bill to your provider account. Sortio doesn't meter your usage at all.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center">
              <span class="mr-2">🎛️</span> Your models, your rates
            </h4>
            <p class="text-[#424245]">Pick any supported model and pay the provider's per-token price.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center">
              <span class="mr-2">✨</span> Premium model access
            </h4>
            <p class="text-[#424245]">Use whatever chat model your provider exposes — Sortio fetches the list from their API and lets you pick.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center">
              <span class="mr-2">🧩</span> Works with everything
            </h4>
            <p class="text-[#424245]">Same KG extraction, same <a href="/docs/rules-engine" class="text-[#06c]">Automations</a>, same <a href="/docs/spaces" class="text-[#06c]">Spaces</a>.</p>
          </div>
        </div>
      `
    },
    {
      id: "when-to-use",
      title: "When to use BYOK vs Cloud vs Ollama",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Sortio supports three AI backends. Each has a different privacy posture, cost model, and setup effort — here's how they compare.
        </p>

        <div class="grid md:grid-cols-3 gap-4 my-8">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-3">Sortio Cloud</h4>
            <div class="space-y-2 text-sm">
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Privacy:</strong> Standard TLS</p>
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Cost:</strong> Included in subscription</p>
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Setup:</strong> Zero</p>
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Best for:</strong> General users who want it to just work</p>
            </div>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-3">Local (Ollama)</h4>
            <div class="space-y-2 text-sm">
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Privacy:</strong> Maximum (fully offline)</p>
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Cost:</strong> Free (hardware only)</p>
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Setup:</strong> Install Ollama + pick a model</p>
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Best for:</strong> Sensitive files or offline use</p>
            </div>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-3">BYOK</h4>
            <div class="space-y-2 text-sm">
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Privacy:</strong> Goes to OpenAI/Anthropic directly</p>
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Cost:</strong> Pay provider per-token</p>
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Setup:</strong> Paste API key</p>
              <p class="text-[#424245]"><strong class="text-[#1d1d1f]">Best for:</strong> You already pay OpenAI or Anthropic directly or want specific premium models</p>
            </div>
          </div>
        </div>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">💡</span>
            Tip: Switch any time
          </h4>
          <p class="text-[#424245]">You can switch providers any time from Settings without re-indexing. Knowledge Graph data persists across provider changes.</p>
        </div>
      `
    },
    {
      id: "supported-providers",
      title: "Supported providers and models",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          BYOK currently supports OpenAI and Anthropic. Paste a key for one or both — Sortio remembers each independently.
        </p>

        <div class="grid md:grid-cols-2 gap-4 my-8">
          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] text-lg mb-4">OpenAI</h4>
            <div class="space-y-3 text-sm">
              <div>
                <p class="text-[#1d1d1f] font-medium mb-1">API key format</p>
                <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">sk-...</code>
              </div>
              <div>
                <p class="text-[#1d1d1f] font-medium mb-1">Get a key</p>
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" class="text-[#06c] break-all">platform.openai.com/api-keys</a>
              </div>
              <div>
                <p class="text-[#1d1d1f] font-medium mb-1">Models</p>
                <p class="text-[#424245]">Sortio fetches your available chat models directly from OpenAI when you click <strong>Load</strong>. Pick whichever makes sense for your workload — smaller models are fast and cheap per call, larger models are more accurate for nuanced extractions.</p>
              </div>
            </div>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] text-lg mb-4">Anthropic</h4>
            <div class="space-y-3 text-sm">
              <div>
                <p class="text-[#1d1d1f] font-medium mb-1">API key format</p>
                <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">sk-ant-...</code>
              </div>
              <div>
                <p class="text-[#1d1d1f] font-medium mb-1">Get a key</p>
                <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" class="text-[#06c] break-all">console.anthropic.com/settings/keys</a>
              </div>
              <div>
                <p class="text-[#1d1d1f] font-medium mb-1">Models</p>
                <p class="text-[#424245]">Sortio fetches your available Claude models from the Anthropic API when you click <strong>Load</strong>. Smaller Claude models handle per-file classification quickly; larger ones are better for complex entity extraction.</p>
              </div>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "setup",
      title: "Setting up BYOK",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Setup takes about a minute. You'll paste your key, test it, and save.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="border-b border-[#e0e0e5] p-5">
            <h4 class="font-semibold text-[#1d1d1f]">Step-by-Step</h4>
          </div>
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">1</span>
                </div>
                Open Settings
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Click the gear icon in the sidebar, or press <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">⌘,</code> (Cmd+comma).</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">2</span>
                </div>
                Select Bring Your Own Key
              </h5>
              <p class="mt-2 text-[#424245] ml-10">In the <strong>AI Provider</strong> section, choose the <strong>BYOK</strong> radio. New options appear below.</p>
            </div>
          </div>
        </div>

        <div class="my-8 rounded-xl overflow-hidden border border-[#e0e0e5]">
          <img src="/screenshots/byok/redacted_settings_byok_openai_empty.png" alt="BYOK form expanded with OpenAI tab selected" class="w-full" />
        </div>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">3</span>
                </div>
                Choose OpenAI or Anthropic
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Use the tab buttons labeled <strong>OpenAI</strong> and <strong>Anthropic</strong>.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">4</span>
                </div>
                Paste your API key
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Paste into the password-masked field. If a key is already saved, you'll see a <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">Current: sk-...xxxx</code> preview below.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">5</span>
                </div>
                Click Load
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Sortio fetches your available models from the provider's API and populates the <strong>Model</strong> dropdown. You can also type a model name directly if needed.</p>
            </div>
          </div>
        </div>

        <div class="my-8 rounded-xl overflow-hidden border border-[#e0e0e5]">
          <img src="/screenshots/byok/redacted_settings_byok_openai_models_loaded.png" alt="Model dropdown populated after clicking Load" class="w-full" />
        </div>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">6</span>
                </div>
                Click Test Key
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Sends a minimal test call. A green <strong>key is valid</strong> means you're wired up; errors are surfaced verbatim.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">7</span>
                </div>
                Click Save
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Commits your configuration. The app header now shows <strong>BYOK (openai)</strong> or <strong>BYOK (anthropic)</strong>.</p>
            </div>
          </div>
        </div>

        <div class="my-8 rounded-xl overflow-hidden border border-[#e0e0e5]">
          <img src="/screenshots/byok/redacted_settings_byok_test_success.png" alt="BYOK setup showing successful key test" class="w-full" />
        </div>

        <div class="mt-8 border border-green-200 rounded-xl p-5 bg-green-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">✅</span>
            You're done
          </h4>
          <p class="text-[#424245]">That's it. Every Sort, every Space index, every Automation lookup now routes through your API key.</p>
        </div>
      `
    },
    {
      id: "security",
      title: "How is my API key stored?",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Your API key never leaves your machine except to call the provider you chose. Here's exactly where it lives and how it's protected.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Local storage only</h4>
              <p class="text-[#424245]">Keys live in Sortio's local SQLite database under setting keys <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">openai_api_key</code> and <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">anthropic_api_key</code>.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Encrypted at rest</h4>
              <p class="text-[#424245]">The database file is encrypted using <strong>SQLCipher</strong> with a machine-specific derived key. The file is useless if copied to another computer.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Transmitted only to the provider</h4>
              <p class="text-[#424245]">Keys are transmitted <strong>only</strong> to <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">api.openai.com</code> or <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">api.anthropic.com</code> over HTTPS. Never sent to Sortio's servers, never logged, never in analytics.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Masked in the UI</h4>
              <p class="text-[#424245]">Display is always masked — the UI only shows <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">sk-...xyz</code>.</p>
            </div>
          </div>
        </div>

        <div class="mt-8 border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">⚠️</span>
            If you suspect a key has been compromised
          </h4>
          <p class="text-[#424245]">Deleting Sortio does <strong>not</strong> remove your API key from OpenAI or Anthropic. If you suspect compromise, revoke the key from your provider's dashboard.</p>
        </div>

        <p class="text-[#424245] mt-6">See <a href="/docs/security" class="text-[#06c]">Security & Privacy</a> for broader disk-encryption details.</p>
      `
    },
    {
      id: "costs",
      title: "Understanding costs",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          With BYOK, Sortio doesn't meter your usage at all. You're billed by OpenAI or Anthropic directly for every call Sortio makes on your behalf.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-4">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Rough guidance</h4>
              <p class="text-[#424245]">Classification per file is small — typically <strong>~500–2k input tokens</strong> and <strong>~100 output tokens</strong>. Indexing a Space of a few hundred files on a small, cheap model is usually well under a dollar. Check your provider's dashboard to confirm cost for your specific model choice.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Provider pricing pages</h4>
              <ul class="list-disc list-inside text-[#424245] space-y-1 ml-2">
                <li><a href="https://openai.com/api/pricing/" target="_blank" rel="noopener noreferrer" class="text-[#06c]">OpenAI pricing</a></li>
                <li><a href="https://www.anthropic.com/api" target="_blank" rel="noopener noreferrer" class="text-[#06c]">Anthropic API pricing</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 mb-6">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">💡</span>
            Tip: Use a cheap model for the classification pass
          </h4>
          <p class="text-[#424245]">Use a cheap/fast model for the per-file classification pass — the bulk of Sortio's work is many small calls, so the cost per file matters more than single-call accuracy. If a particular Space has unusually dense or nuanced content, switch to a stronger model and re-index just that Space.</p>
        </div>

        <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">ℹ️</span>
            No in-app cost estimate yet
          </h4>
          <p class="text-[#424245]">Sortio doesn't currently show a per-call cost estimate in the app for BYOK. Monitor spend on your provider's dashboard.</p>
        </div>
      `
    },
    {
      id: "switching",
      title: "Switching providers and troubleshooting",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Switching is as simple as changing the <strong>AI Provider</strong> radio and clicking <strong>Save</strong>. Keys and model selections for each provider persist independently — you can flip between OpenAI and Anthropic without re-entering keys.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="border-b border-[#e0e0e5] p-5">
            <h4 class="font-semibold text-[#1d1d1f]">Troubleshooting</h4>
          </div>
          <div class="p-6 space-y-6">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">"Invalid API key" on Test Key</h4>
              <p class="text-[#424245]">Verify the key in your provider dashboard, check for stray whitespace when pasting, and confirm billing is set up on the provider side.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">"Load" returns nothing or fails</h4>
              <p class="text-[#424245]">Save the key first — Sortio reads the saved key for the model fetch. For Anthropic, the fallback list is used if the live fetch fails.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Rate limits / 429 errors</h4>
              <p class="text-[#424245]">Both providers throttle new accounts. Upgrade your tier with the provider, or switch to a smaller model.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">I changed my key upstream</h4>
              <p class="text-[#424245]">Paste the new key, click <strong>Save</strong>, then <strong>Test Key</strong> to confirm.</p>
            </div>
          </div>
        </div>

        <div class="mt-8 border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] mb-3">Related documentation</h4>
          <ul class="list-disc list-inside text-[#424245] space-y-1 ml-2">
            <li><a href="/docs/local-models" class="text-[#06c]">Offline Mode Setup</a> — run Ollama locally for maximum privacy</li>
            <li><a href="/docs/spaces" class="text-[#06c]">Spaces</a> — monitored folders powered by your chosen AI</li>
            <li><a href="/docs/rules-engine" class="text-[#06c]">Rules Engine</a> — deterministic Automations that run before AI</li>
          </ul>
        </div>
      `
    }
  ]
};

export default byokContent;
